import { CREATE_CONVERSATION_API } from '~/routes/remix-api.chat.create-conversation';
import { CREATE_CONVERSATION_MESSAGE_API } from '~/routes/remix-api.chat.create-conversation-message';
import { MARK_CONVERSATION_READ_API } from '~/routes/remix-api.chat.mark-conversation-read';
import { MARK_CONVERSATION_UNREAD_API } from '~/routes/remix-api.chat.mark-conversation-unread';
import { UPDATE_CONVERSATION_API } from '~/routes/remix-api.chat.update-conversation';
import { UPDATE_CONVERSATION_THUMBNAIL_API } from '~/routes/remix-api.chat.update-thumbnail';
import { LEAVE_CONVERSATION_API } from '~/routes/remix-api.chat.leave-conversation';
import { ADD_PARTICIPANTS_API } from '~/routes/remix-api.chat.add-participants';
import { REMOVE_PARTICIPANT_API } from '~/routes/remix-api.chat.remove-participant';
import { INVITE_TO_GROUP_CHAT_API } from '~/routes/remix-api.chat.invite-to-group-chat';
import { UPDATE_CONVERSATION_STATUS_API } from '~/routes/remix-api.chat.update-conversation-status';
import { MUTE_CONVERSATION_API } from '~/routes/remix-api.chat.mute';
import { UPDATE_CONVERSATION_SETTINGS_API } from '~/routes/remix-api.chat.update-settings';
import { REACT_MESSAGE_API } from '~/routes/remix-api.chat.react-message';
import { DELETE_MESSAGE_API } from '~/routes/remix-api.chat.delete-message';
import { UPDATE_NOTIFICATION_SETTINGS_API } from '~/routes/remix-api.settings.notifications';
import { SEND_MARKETING_PREFERENCES_EMAIL_API } from '~/routes/remix-api.settings.marketing-preferences-email';
import { ACTION_ADD_ITEMS_TO_MOOD_BOARD } from '~/routes/actions.add-items-to-mood-board';
import { REMOVE_INSPIRATION_ITEMS_BY_PAGE_API_ROUTE } from '~/routes/actions.remove-items-from-mood-board-by-page';
import { REMOVE_BUILD_HUB_INSPIRATION_API_ROUTE } from '~/routes/remix-api.app.build-hub-inspiration-remove';

/** Sync saved module API route - doesn't need revalidation since it updates in place */
const SYNC_SAVED_MODULE_API = '/remix-api/page-builder/sync-saved-module';

/**
 * Routes that should not trigger a revalidation when the form is submitted.
 */
const NOT_REVALIDATE_ROUTES = [
    CREATE_CONVERSATION_API,
    CREATE_CONVERSATION_MESSAGE_API,
    MARK_CONVERSATION_READ_API,
    MARK_CONVERSATION_UNREAD_API,
    UPDATE_CONVERSATION_API,
    UPDATE_CONVERSATION_THUMBNAIL_API,
    LEAVE_CONVERSATION_API,
    ADD_PARTICIPANTS_API,
    REMOVE_PARTICIPANT_API,
    INVITE_TO_GROUP_CHAT_API,
    UPDATE_CONVERSATION_STATUS_API,
    MUTE_CONVERSATION_API,
    UPDATE_CONVERSATION_SETTINGS_API,
    REACT_MESSAGE_API,
    DELETE_MESSAGE_API,
    SYNC_SAVED_MODULE_API,
    UPDATE_NOTIFICATION_SETTINGS_API,
    SEND_MARKETING_PREFERENCES_EMAIL_API,
    ACTION_ADD_ITEMS_TO_MOOD_BOARD,
    REMOVE_INSPIRATION_ITEMS_BY_PAGE_API_ROUTE,
    REMOVE_BUILD_HUB_INSPIRATION_API_ROUTE,
] as const;

/**
 * Same as above, but only for the root _app.tsx route.
 */
const ROOT_NOT_REVALIDATE_ROUTES = NOT_REVALIDATE_ROUTES.filter((route) => {
    return ![
        // Mark conversation read/unread will update the notification count.
        MARK_CONVERSATION_READ_API,
        MARK_CONVERSATION_UNREAD_API,
        // Marking conversation as archived/done may update the notification count.
        UPDATE_CONVERSATION_STATUS_API,
        // Leave conversation may update notification count.
        LEAVE_CONVERSATION_API,
        // Invite to group chat may update notification count.
        INVITE_TO_GROUP_CHAT_API,
        // Mute conversation may update notification count.
        MUTE_CONVERSATION_API,
    ].includes(route);
});

/**
 * Check if a route loader should trigger a revalidation when a form is
 * submitted. If the form action is in a predefined list, the loader should not
 * trigger a revalidation. This is a performance optimization to avoid
 * unnecessary revalidations.
 * @param formAction The form action (route) to check.
 * @returns The route loader should not revalidate.
 * @see {@link shouldNotRevalidateRoot} for the root `_app.tsx` route.
 */
export function shouldNotRevalidate(formAction: string | undefined) {
    if (!formAction) {
        return false;
    }
    return NOT_REVALIDATE_ROUTES.includes(formAction);
}

/**
 * Check if the root `_app.tsx` route loader should trigger a revalidation when
 * a form is submitted. This is the same as `shouldNotRevalidate`, but only for
 * the root `_app.tsx` route.
 * @param formAction The form action (route) to check.
 * @returns The root `_app.tsx` route loader should not revalidate.
 * @see {@link shouldNotRevalidate} for routes except for the root `_app.tsx` route.
 */
export function shouldNotRevalidateRoot(formAction: string | undefined) {
    if (!formAction) {
        return false;
    }
    return ROOT_NOT_REVALIDATE_ROUTES.includes(formAction);
}
